/**
 * PostNLAssignedCard — shown on an employee's admin profile. Lists the
 * PostNL codes currently assigned to the user (grouped by firm), with
 * quick actions to assign a suggested code or unassign an existing one.
 */
import { useEffect, useState } from 'react';
import { api } from '../lib/auth';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Label } from './ui/label';
import { Input } from './ui/input';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from './ui/dialog';
import { Package, Plus, X, Loader2, Sparkles, Search } from 'lucide-react';

export default function PostNLAssignedCard({ userId, firms = [] }) {
  const [assigned, setAssigned] = useState([]);
  const [loading, setLoading] = useState(true);
  const [assignOpen, setAssignOpen] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [searchResults, setSearchResults] = useState([]);
  const [search, setSearch] = useState('');
  const [searchFirm, setSearchFirm] = useState('');
  const [busy, setBusy] = useState(false);

  const firmName = (id) => firms.find((f) => f.id === id)?.name || id;

  const fetchAssigned = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/postnl/codes/by-user/${userId}`);
      setAssigned(res.data?.data || []);
    } catch { /* silent */ }
    finally { setLoading(false); }
  };

  useEffect(() => {
    if (userId) fetchAssigned();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  // When the assign dialog opens, fetch the suggestions + default search.
  useEffect(() => {
    if (!assignOpen || !userId) return;
    (async () => {
      try {
        const res = await api.get(`/postnl/codes/suggest/${userId}`);
        setSuggestions(res.data?.data || []);
      } catch { setSuggestions([]); }
    })();
  }, [assignOpen, userId]);

  // Debounced unassigned-code search
  useEffect(() => {
    if (!assignOpen) return;
    const handle = setTimeout(async () => {
      try {
        const params = new URLSearchParams();
        params.set('unassigned_only', 'true');
        if (searchFirm) params.set('firm_id', searchFirm);
        if (search.trim()) params.set('q', search.trim());
        const res = await api.get(`/postnl/codes?${params.toString()}`);
        setSearchResults(res.data?.data || []);
      } catch { setSearchResults([]); }
    }, 250);
    return () => clearTimeout(handle);
  }, [assignOpen, search, searchFirm]);

  const assign = async (codeId) => {
    setBusy(true);
    try {
      await api.post(`/postnl/codes/${codeId}/assign`, { user_id: userId });
      toast.success('Kod atandı');
      setAssignOpen(false);
      fetchAssigned();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Atama hatası');
    } finally {
      setBusy(false);
    }
  };

  const unassign = async (codeId) => {
    try {
      await api.post(`/postnl/codes/${codeId}/assign`, { user_id: null });
      toast.success('Atama kaldırıldı');
      fetchAssigned();
    } catch {
      toast.error('Kaldırılamadı');
    }
  };

  return (
    <div className="mt-4 pt-4 border-t border-slate-200 dark:border-slate-600" data-testid="postnl-assigned-card">
      <div className="flex items-center justify-between mb-3">
        <Label className="flex items-center gap-2">
          <Package className="h-4 w-4 text-orange-600" />
          PostNL Kodları
        </Label>
        <Dialog open={assignOpen} onOpenChange={setAssignOpen}>
          <DialogTrigger asChild>
            <Button size="sm" variant="outline" data-testid="postnl-assigned-add-btn">
              <Plus className="h-4 w-4 mr-1" /> Kod Ata
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>PostNL Kodu Ata</DialogTitle>
              <DialogDescription>
                Önerilen kodlar çalışanın soyad / lakaplarıyla eşleşenlerdir. Listede yoksa firma/arama ile bulabilirsin.
              </DialogDescription>
            </DialogHeader>

            {suggestions.length > 0 && (
              <div>
                <Label className="flex items-center gap-2 mb-2 text-emerald-700 dark:text-emerald-400">
                  <Sparkles className="h-3.5 w-3.5" />
                  Önerilenler ({suggestions.length})
                </Label>
                <div className="flex flex-wrap gap-2 mb-3">
                  {suggestions.map((s) => (
                    <button
                      key={s.id}
                      type="button"
                      disabled={busy}
                      onClick={() => assign(s.id)}
                      className="inline-flex items-center gap-2 px-3 py-1.5 text-sm rounded-full border border-emerald-300 bg-emerald-50 hover:bg-emerald-100 dark:bg-emerald-900/20 dark:border-emerald-700 dark:hover:bg-emerald-900/40 transition-colors"
                      data-testid={`postnl-suggest-${s.id}`}
                    >
                      <span className="font-mono font-semibold">{s.code}</span>
                      <span className="text-slate-600 dark:text-slate-300">{s.surname} {s.initials}</span>
                      <Badge variant="outline" className="text-[10px] px-1 py-0">{firmName(s.firm_id)}</Badge>
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="flex flex-col sm:flex-row gap-2">
              <select
                value={searchFirm}
                onChange={(e) => setSearchFirm(e.target.value)}
                className="text-sm rounded-md border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 px-2 py-1.5"
                data-testid="postnl-search-firm"
              >
                <option value="">Tüm Firmalar</option>
                {firms.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
              </select>
              <div className="relative flex-1">
                <Search className="h-4 w-4 absolute left-3 top-2.5 text-slate-400" />
                <Input
                  placeholder="Kod veya isim ara…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-9"
                  data-testid="postnl-search-input"
                />
              </div>
            </div>
            <div className="max-h-64 overflow-y-auto mt-2 border rounded-md divide-y">
              {searchResults.length === 0 ? (
                <p className="text-xs text-slate-400 text-center py-4">Sonuç yok.</p>
              ) : searchResults.map((r) => (
                <button
                  key={r.id}
                  type="button"
                  disabled={busy}
                  onClick={() => assign(r.id)}
                  className="w-full flex items-center gap-3 px-3 py-2 text-sm hover:bg-slate-50 dark:hover:bg-slate-800"
                  data-testid={`postnl-search-result-${r.id}`}
                >
                  <span className="font-mono font-semibold w-20">{r.code}</span>
                  <span className="flex-1 text-left">{r.surname} {r.initials}</span>
                  <Badge variant="outline" className="text-[10px] px-1 py-0">{firmName(r.firm_id)}</Badge>
                </button>
              ))}
            </div>

            <DialogFooter>
              <Button variant="ghost" onClick={() => setAssignOpen(false)}>Kapat</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-4 text-slate-400">
          <Loader2 className="h-4 w-4 animate-spin" />
        </div>
      ) : assigned.length === 0 ? (
        <p className="text-xs text-slate-400 py-2">Henüz atanmış PostNL kodu yok.</p>
      ) : (
        <div className="flex flex-wrap gap-2">
          {assigned.map((c) => (
            <span
              key={c.id}
              className="group inline-flex items-center gap-2 pl-3 pr-2 py-1.5 text-sm rounded-full border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800"
              data-testid={`postnl-assigned-chip-${c.id}`}
            >
              <span className="font-mono font-semibold">{c.code}</span>
              <span className="text-slate-500 dark:text-slate-400 text-xs">
                {c.firm_name || firmName(c.firm_id)}
              </span>
              <button
                type="button"
                onClick={() => unassign(c.id)}
                className="opacity-40 group-hover:opacity-100 text-red-500 hover:text-red-600 transition-opacity"
                title="Atamayı kaldır"
                data-testid={`postnl-unassign-${c.id}`}
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
